import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  customerActionsApi,
  type AccountActionResult,
  type CardActionResult,
} from '../../api/customer-actions.api.js';
import { useAuth } from '../../auth/useAuth.js';

import { SignInDetailsCard } from './SignInDetailsCard.js';

type ActionKind =
  | 'freeze-account'
  | 'suspend-account'
  | 'close-account'
  | 'unfreeze-account'
  | 'freeze-card'
  | 'cancel-card';

interface ActionDef {
  kind: ActionKind;
  label: string;
  target: 'account' | 'card';
}

const ACTIONS: readonly ActionDef[] = [
  { kind: 'freeze-account', label: 'Freeze account', target: 'account' },
  { kind: 'suspend-account', label: 'Suspend account', target: 'account' },
  { kind: 'close-account', label: 'Close account', target: 'account' },
  { kind: 'unfreeze-account', label: 'Unfreeze account', target: 'account' },
  { kind: 'freeze-card', label: 'Freeze card', target: 'card' },
  { kind: 'cancel-card', label: 'Cancel card', target: 'card' },
];

interface ActionHistoryEntry {
  timestamp: string;
  action: string;
  outcome: string;
}

export function CustomerActionsPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const customerId = params.id ?? '';
  const { can } = useAuth();
  const [activeAction, setActiveAction] = useState<ActionDef | null>(null);
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [history, setHistory] = useState<ActionHistoryEntry[]>([]);

  const accountMutation = useMutation({
    mutationFn: async (args: { kind: ActionKind; accountId: string; reason: string }) => {
      const fn =
        args.kind === 'freeze-account'
          ? customerActionsApi.freezeAccount
          : args.kind === 'suspend-account'
            ? customerActionsApi.suspendAccount
            : args.kind === 'close-account'
              ? customerActionsApi.closeAccount
              : customerActionsApi.unfreezeAccount;
      return fn(customerId, args.accountId, args.reason);
    },
    onSuccess: (result: AccountActionResult) => {
      setToast(`Success: new state ${result.state}`);
      setHistory((prev) => [
        { timestamp: new Date().toISOString(), action: activeAction?.label ?? '', outcome: result.state },
        ...prev,
      ]);
      closeDialog();
    },
    onError: (err: unknown) => {
      setToast(err instanceof Error ? err.message : 'Action failed');
    },
  });

  const cardMutation = useMutation({
    mutationFn: async (args: { kind: ActionKind; cardId: string; reason: string }) => {
      const fn =
        args.kind === 'freeze-card'
          ? customerActionsApi.freezeCard
          : customerActionsApi.cancelCard;
      return fn(customerId, args.cardId, args.reason);
    },
    onSuccess: (result: CardActionResult) => {
      setToast(`Success: card state ${result.state}`);
      setHistory((prev) => [
        { timestamp: new Date().toISOString(), action: activeAction?.label ?? '', outcome: result.state },
        ...prev,
      ]);
      closeDialog();
    },
    onError: (err: unknown) => {
      setToast(err instanceof Error ? err.message : 'Action failed');
    },
  });

  const closeDialog = (): void => {
    setActiveAction(null);
    setTargetId('');
    setReason('');
  };

  const onSubmit = (): void => {
    if (activeAction === null) {
      return;
    }
    if (targetId.trim().length === 0 || reason.trim().length === 0) {
      return;
    }
    if (activeAction.target === 'account') {
      accountMutation.mutate({ kind: activeAction.kind, accountId: targetId, reason });
    } else {
      cardMutation.mutate({ kind: activeAction.kind, cardId: targetId, reason });
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Customer actions
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Customer ID: {customerId}
      </Typography>

      <Card sx={{ mt: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available actions
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {ACTIONS.map((action) => (
              <Button
                key={action.kind}
                variant="outlined"
                onClick={() => setActiveAction(action)}
                sx={{ m: 0.5 }}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {can('customer.access') ? <SignInDetailsCard customerId={customerId} /> : null}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Action history
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      No actions yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((entry, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{entry.timestamp}</TableCell>
                    <TableCell>{entry.action}</TableCell>
                    <TableCell>{entry.outcome}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={activeAction !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{activeAction?.label}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ my: 2 }}>
            This action will be audit-logged and cannot be undone without explicit admin intervention.
          </Alert>
          <Stack spacing={2}>
            <TextField
              label={activeAction?.target === 'account' ? 'Account ID' : 'Card ID'}
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={onSubmit}
            disabled={
              accountMutation.isPending ||
              cardMutation.isPending ||
              targetId.trim().length === 0 ||
              reason.trim().length === 0
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
}
